import { useMutation, useQuery } from '@tanstack/react-query';

import companyValueQuestionService from './companyValueQuestionService';

import type { ReviewQuestionTranslationsUpdateRequest } from './companyValueQuestionService';
import type { ReviewQuestion } from '@/types/review/ReviewQuestion';

import { queryClient } from '@/AppWithProviders';
import {
    CompanyValueQuestionMutationKey,
    CompanyValueQuestionQueryKey,
} from '@/constants/QueryKeys';

export const useGetCompanyValueQuestions = (companyValueId: string) => {
    return useQuery<ReviewQuestion[]>({
        queryKey: [CompanyValueQuestionQueryKey.COMPANY_VALUE_QUESTIONS, companyValueId],
        queryFn: () => companyValueQuestionService.getQuestionsByCompanyValueId(companyValueId),
        enabled: !!companyValueId,
    });
};

export const useCreateCompanyValueQuestion = () => {
    return useMutation<ReviewQuestion, Error, ReviewQuestion>({
        mutationKey: [CompanyValueQuestionMutationKey.CREATE_COMPANY_VALUE_QUESTION],
        mutationFn: (request) => companyValueQuestionService.createQuestion(request),
        onSuccess: (_, variables) => {
            if (variables.companyValueId) {
                queryClient.invalidateQueries({
                    queryKey: [
                        CompanyValueQuestionQueryKey.COMPANY_VALUE_QUESTIONS,
                        variables.companyValueId,
                    ],
                });
            }
        },
    });
};

export const useUpdateCompanyValueQuestion = () => {
    return useMutation<
        ReviewQuestion,
        Error,
        { id: string; companyValueId: string; request: ReviewQuestion }
    >({
        mutationKey: [CompanyValueQuestionMutationKey.UPDATE_COMPANY_VALUE_QUESTION],
        mutationFn: ({ id, request }) => companyValueQuestionService.updateQuestion(id, request),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: [
                    CompanyValueQuestionQueryKey.COMPANY_VALUE_QUESTIONS,
                    variables.companyValueId,
                ],
            });
        },
    });
};

export const useUpdateCompanyValueQuestionTranslations = () => {
    return useMutation<
        ReviewQuestion,
        Error,
        { id: string; companyValueId: string; request: ReviewQuestionTranslationsUpdateRequest }
    >({
        mutationKey: [CompanyValueQuestionMutationKey.UPDATE_COMPANY_VALUE_QUESTION_TRANSLATIONS],
        mutationFn: ({ id, request }) =>
            companyValueQuestionService.updateQuestionTranslations(id, request),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: [
                    CompanyValueQuestionQueryKey.COMPANY_VALUE_QUESTIONS,
                    variables.companyValueId,
                ],
            });
        },
    });
};

export const useReorderCompanyValueQuestions = () => {
    return useMutation<
        ReviewQuestion[],
        Error,
        { companyValueId: string; questions: ReviewQuestion[] },
        { previous?: ReviewQuestion[] }
    >({
        mutationKey: [CompanyValueQuestionMutationKey.REORDER_COMPANY_VALUE_QUESTIONS],
        mutationFn: ({ companyValueId, questions }) =>
            companyValueQuestionService.reorderQuestions(
                companyValueId,
                questions.map((question) => question.id).filter((id): id is string => Boolean(id)),
            ),
        onMutate: ({ companyValueId, questions }) => {
            const queryKey = [CompanyValueQuestionQueryKey.COMPANY_VALUE_QUESTIONS, companyValueId];
            void queryClient.cancelQueries({ queryKey });
            const previous = queryClient.getQueryData<ReviewQuestion[]>(queryKey);

            queryClient.setQueryData<ReviewQuestion[]>(
                queryKey,
                questions.map((question, sortOrder) => ({ ...question, sortOrder })),
            );

            return { previous };
        },
        onError: (_error, variables, context) => {
            if (context?.previous) {
                queryClient.setQueryData(
                    [
                        CompanyValueQuestionQueryKey.COMPANY_VALUE_QUESTIONS,
                        variables.companyValueId,
                    ],
                    context.previous,
                );
            }
        },
        onSuccess: (data, variables) => {
            queryClient.setQueryData(
                [CompanyValueQuestionQueryKey.COMPANY_VALUE_QUESTIONS, variables.companyValueId],
                data,
            );
        },
    });
};

export const useDeleteCompanyValueQuestion = () => {
    return useMutation<ReviewQuestion, Error, { id: string; companyValueId: string }>({
        mutationKey: [CompanyValueQuestionMutationKey.DELETE_COMPANY_VALUE_QUESTION],
        mutationFn: ({ id }) => companyValueQuestionService.deleteQuestion(id),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: [
                    CompanyValueQuestionQueryKey.COMPANY_VALUE_QUESTIONS,
                    variables.companyValueId,
                ],
            });
        },
    });
};

export const useToggleCompanyValueQuestionActive = () => {
    return useMutation<ReviewQuestion, Error, { id: string; companyValueId: string }>({
        mutationKey: [CompanyValueQuestionMutationKey.TOGGLE_COMPANY_VALUE_QUESTION_ACTIVE],
        mutationFn: ({ id }) => companyValueQuestionService.toggleActive(id),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: [
                    CompanyValueQuestionQueryKey.COMPANY_VALUE_QUESTIONS,
                    variables.companyValueId,
                ],
            });
        },
    });
};
